import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { CommunityPost } from '../types';
import { share } from '../utils/share';

type LoadState = 'loading' | 'ready' | 'unavailable' | 'error';

const DEFAULT_AVATAR = '/avatar-default.png';
const APP_URL = '/app';
const PENDING_PUBLIC_POST_SHARE_STORAGE_KEY = 'roddo_pending_public_post_share_id';

function buildAppPostUrl(postId: string): string {
  const clean = String(postId || '').trim();
  if (!clean) return APP_URL;
  return `${APP_URL}?postId=${encodeURIComponent(clean)}`;
}

function rememberAppPostTarget(postId: string) {
  const clean = String(postId || '').trim();
  if (!clean || typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PENDING_PUBLIC_POST_SHARE_STORAGE_KEY, clean);
  } catch {}
}

function cleanString(value: unknown, fallback = ''): string {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function getPostIdFromPath(pathname = window.location.pathname): string {
  const normalized = pathname.replace(/\/+$/, '');
  const marker = '/app/p/';
  const idx = normalized.indexOf(marker);
  if (idx < 0) return '';
  return decodeURIComponent(normalized.slice(idx + marker.length).split('/')[0] || '').trim();
}

function firstImage(post: any): string {
  if (Array.isArray(post?.images)) {
    const found = post.images.find((v: unknown) => typeof v === 'string' && v.trim());
    if (found) return String(found).trim();
  }
  return cleanString(post?.imageUrl || post?.image || post?.photoUrl || post?.photoURL);
}

function getCreatedAt(post: any): number {
  const raw = post?.timestamp ?? post?.createdAt ?? post?.createdAtMs ?? post?.date;
  if (typeof raw === 'number') return raw;
  if (raw?.toMillis) return raw.toMillis();
  const parsed = Date.parse(String(raw || ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

function isPublicRenderable(post: any): boolean {
  const visibility = cleanString(post?.visibility || 'public').toLowerCase();
  const status = cleanString(post?.status || 'visible').toLowerCase();
  return visibility !== 'private' && visibility !== 'hidden' && status !== 'hidden' && status !== 'pending_review';
}

function formatDate(ms: number): string {
  if (!ms) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(ms));
  } catch {
    return '';
  }
}

function upsertMeta(nameOrProperty: string, value: string, property = false) {
  if (typeof document === 'undefined' || !value) return;
  const attr = property ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${nameOrProperty}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, nameOrProperty);
    document.head.appendChild(el);
  }
  el.content = value;
}

export default function PublicPostPreviewPage() {
  const { t } = useTranslation();
  const postId = useMemo(() => getPostIdFromPath(), []);
  const [state, setState] = useState<LoadState>('loading');
  const [post, setPost] = useState<CommunityPost | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!postId) {
        setState('unavailable');
        return;
      }

      try {
        setState('loading');
        const snap = await getDoc(doc(db, 'communityPosts', postId));
        if (cancelled) return;

        if (!snap.exists()) {
          setState('unavailable');
          return;
        }

        const data = { id: snap.id, ...(snap.data() as any) } as CommunityPost;
        if (!isPublicRenderable(data)) {
          setState('unavailable');
          return;
        }

        setPost(data);
        setState('ready');
      } catch (err) {
        console.error('[PublicPostPreviewPage] load failed', err);
        if (!cancelled) setState('error');
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [postId]);

  const authorName = cleanString((post as any)?.author || (post as any)?.authorName || (post as any)?.displayName, t('publicPost.authorFallback', 'A Roddo angler'));
  const authorAvatar = cleanString((post as any)?.authorAvatar || (post as any)?.avatar || (post as any)?.photoURL, DEFAULT_AVATAR);
  const caption = cleanString((post as any)?.caption || (post as any)?.text || (post as any)?.body);
  const species = cleanString((post as any)?.species || (post as any)?.fishSpecies || (post as any)?.catchData?.species);
  const location = cleanString((post as any)?.location || (post as any)?.waterName || (post as any)?.catchData?.waterName);
  const image = firstImage(post);
  const createdAt = formatDate(getCreatedAt(post));
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const appUrl = buildAppPostUrl(postId);

  useEffect(() => {
    if (!post) return;
    const title = species ? `${authorName} shared a ${species} on Roddo` : `${authorName} shared a catch on Roddo`;
    const description = caption || [species, location].filter(Boolean).join(' at ') || t('publicPost.ogFallback', 'Open this Baleen Bay post in Roddo.');
    document.title = title;
    upsertMeta('description', description.slice(0, 180));
    upsertMeta('og:title', title, true);
    upsertMeta('og:description', description.slice(0, 180), true);
    upsertMeta('og:type', 'article', true);
    upsertMeta('og:url', shareUrl, true);
    if (image) upsertMeta('og:image', image, true);
    upsertMeta('twitter:card', image ? 'summary_large_image' : 'summary');
  }, [authorName, caption, image, location, post, shareUrl, species]);

  const shellClass = 'min-h-screen w-screen overflow-y-auto bg-[#f7efe6] text-[#2b2b2b] dark:bg-[#070B15] dark:text-[#F4F6FA]';
  const cardClass = 'rounded-[18px] border border-black/10 bg-white/80 p-4 shadow-[0_18px_55px_rgba(15,23,42,0.12)] backdrop-blur dark:border-white/12 dark:bg-[#101827]/96 dark:shadow-[0_12px_32px_rgba(0,0,0,0.30)]';

  if (state === 'loading') {
    return (
      <main className={shellClass}>
        <div className="mx-auto flex min-h-screen max-w-2xl items-center justify-center p-5">
          <section className={cardClass}>
            <div className="text-center">
              <div className="text-4xl">🐟</div>
              <h1 className="mt-3 text-2xl font-black">{t('publicPost.loadingTitle', 'Loading this Baleen Bay post…')}</h1>
              <p className="mt-2 text-sm opacity-70">{t('publicPost.loadingCopy', 'Warming up the waters.')}</p>
            </div>
          </section>
        </div>
      </main>
    );
  }

  if (state !== 'ready' || !post) {
    return (
      <main className={shellClass}>
        <div className="mx-auto flex min-h-screen max-w-2xl items-center justify-center p-5">
          <section className={cardClass}>
            <div className="text-center">
              <div className="text-4xl">🌊</div>
              <h1 className="mt-3 text-2xl font-black">{t('publicPost.unavailableTitle', 'This post is not available')}</h1>
              <p className="mt-2 text-sm leading-6 opacity-75">
                {t('publicPost.unavailableCopy', 'It may have been removed, made private, or needs you to open Roddo to view it.')}
              </p>
              <a href={APP_URL} className="mt-5 inline-flex rounded-full bg-[#2F80ED] dark:bg-[#8A70F2] px-5 py-3 text-sm font-black text-white shadow-lg">
                {t('publicPost.openRoddo', 'Open Roddo')}
              </a>
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className={shellClass}>
      <div className="mx-auto max-w-2xl px-4 py-5 sm:py-8">
        <section className="overflow-hidden rounded-[20px] border border-black/10 bg-white/80 shadow-[0_18px_55px_rgba(15,23,42,0.14)] backdrop-blur dark:border-white/12 dark:bg-[#101827]/98 dark:shadow-[0_14px_36px_rgba(0,0,0,0.32)]">
          <div className="bg-[#F2EBE1] p-4 dark:bg-[#0C1423]">
            <div className="flex items-center gap-3">
              <img src={authorAvatar} alt={authorName} className="h-12 w-12 rounded-2xl border-2 border-white/70 object-cover shadow" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-black">{authorName}</div>
                <div className="text-xs font-bold opacity-65">{t('publicPost.sharedFrom', 'Shared from Baleen Bay')}{createdAt ? ` · ${createdAt}` : ''}</div>
              </div>
              <span className="rounded-full bg-white/70 px-3 py-1 text-[11px] font-black text-[#2168C9] dark:bg-[#0C1423] dark:text-[#C9C2FF]">Roddo</span>
            </div>
          </div>

          {image ? <img src={image} alt={species ? `${species} catch` : 'Roddo catch'} className="max-h-[620px] w-full object-cover" /> : null}

          <div className="space-y-4 p-4 sm:p-5">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.2em] opacity-55">{t('publicPost.postLabel', 'Baleen Bay post')}</div>
              <h1 className="mt-2 text-3xl font-black leading-tight">
                {species ? `${species}${location ? ` at ${location}` : ''}` : t('publicPost.freshCatch', 'A fresh catch from Roddo')}
              </h1>
            </div>

            {caption ? <p className="whitespace-pre-line text-base leading-7 opacity-90">{caption}</p> : null}

            {(species || location) && (
              <div className="flex flex-wrap gap-2">
                {species ? <span className="rounded-full bg-[#2F80ED]/10 px-3 py-1.5 text-xs font-black text-[#2168C9] dark:bg-[#8A70F2]/15 dark:text-[#C9C2FF]">🐟 {species}</span> : null}
                {location ? <span className="rounded-full bg-[#F2EBE1] px-3 py-1.5 text-xs font-black text-[#6E6255] dark:bg-[#0C1423] dark:text-[#AEB8C9]">📍 {location}</span> : null}
              </div>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <a
                href={appUrl}
                onClick={() => rememberAppPostTarget(postId)}
                className="inline-flex items-center justify-center rounded-2xl bg-[#2F80ED] dark:bg-[#8A70F2] px-5 py-3 text-sm font-black text-white shadow-lg"
              >
                {t('publicPost.openInRoddo', 'Open in Roddo')}
              </a>
              <button
                type="button"
                onClick={() => {
                  void share({ url: shareUrl });
                }}
                className="rounded-2xl border border-black/10 bg-black/5 px-5 py-3 text-sm font-black dark:border-white/10 dark:bg-white/10"
              >
                {t('publicPost.shareCatch', 'Share this catch')}
              </button>
            </div>

            <p className="text-center text-xs leading-5 opacity-60">
              {t('publicPost.footer', 'Roddo helps anglers log catches, learn patterns, and share fishing moments from Big Blue.')}
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
